/* istanbul ignore file */
import { match } from 'ts-pattern'
import _ from 'lodash'
import classnames from 'classnames/bind'
import logger from '@adc/logger'
import {
  utils as blueprintUtils,
  primitives as blueprintPrimitives,
} from '@adc/blueprint'
import type { LeadIntakeInput } from '@adc/sdk/types'
import configment from '@adc/configment'
import {
  Button,
  useApplicationNotifications,
  NOTIFICATION_ROLES,
  Text,
  UserService,
  useApplicationContext,
} from '@adc/ui-adc'
import { trackConversationalFormSubmit } from 'common/services/analytics'
import { upsertLeadIntake } from 'common/providers/privateSeller'
import { Step } from '../steps'
import { StepId } from '../steps/types'
import { useStepManagerContext } from '../steps/stepManager'
import {
  PRIVACY_STATEMENT_ROUTE,
  TERMS_OF_USE_ROUTE,
} from 'common/constants/terms'
import styles from '../styles.scss'

const cx = classnames.bind(styles)

const { extractMarketingSupportPrefixes } = blueprintUtils

type UserCreateResponse = {
  body?: {
    user?: {
      user_id?: string
    }
  }
}

const ButtonSection = ({ currentStep }: { currentStep: Step }) => {
  const notifications = useApplicationNotifications()
  const { data, actions } = useStepManagerContext()
  const { user: untypedUser } = useApplicationContext()
  const user: blueprintPrimitives.AuctionUser & { user_id?: string } =
    untypedUser as blueprintPrimitives.AuctionUser

  const { stepProgression, leadIntake, listingId } = data
  const {
    setIsLoading,
    onPreviousStep,
    onNextStep,
    setListingId,
    setLeadIntake,
  } = actions
  const isConfirmAddress = currentStep.id === StepId.ConfirmAddress
  const isAuthentication = currentStep.id === StepId.WelcomeBack
  const isFirstStep = currentStep.id === StepId.Address
  const isLastStep =
    currentStep.isLastStep && currentStep.isLastStep(leadIntake)

  const isSubmitStep =
    currentStep.isSubmitStep && currentStep.isSubmitStep(leadIntake)

  const createLead = async (
    e: unknown,
    leadIntakeInput: LeadIntakeInput,
    needsAuthentication: boolean,
    userId?: string
  ) => {
    try {
      setIsLoading(true)
      const response = await upsertLeadIntake({ input: leadIntakeInput })
      const listingId =
        (response?.properties && response.properties[0]?.listing_id) ||
        undefined
      if (listingId) {
        trackConversationalFormSubmit({ userId })
        setListingId(listingId)
        onNextStep(e, listingId, needsAuthentication)
      } else {
        notifications.toast.open({
          role: NOTIFICATION_ROLES.ERROR,
          title: 'Unsuccessful Listing creation',
          message: 'The lead was missing required information',
        })
        logger.error('Lead intake missing listing_id')
      }
      if (needsAuthentication) {
        setIsLoading(false)
      }
    } catch (error) {
      const { message = '' } = error as Error
      notifications.toast.open({
        role: NOTIFICATION_ROLES.ERROR,
        title: 'Something went wrong',
        message,
      })
      logger.error(JSON.stringify(error))
      setIsLoading(false)
    }
  }

  const removeUnusedLeadAttributes = () => {
    const filteredParams = extractMarketingSupportPrefixes(window.location.href)

    let leadIntakeInput = _.isEmpty(filteredParams?.marketing_source)
      ? {}
      : ({
          marketing_source: filteredParams?.marketing_source,
        } as LeadIntakeInput)

    Object.entries(leadIntake)
      .filter(([, value]) => value !== '')
      .forEach(([key, value]) => {
        if (key === 'property.street_unit') {
          key = 'property.unit_number'
        }
        if (
          key === 'property.bedrooms' ||
          key === 'property.bathrooms' ||
          key === 'property.lot_size'
        ) {
          leadIntakeInput = _.set(
            leadIntakeInput,
            key,
            parseFloat(value ? value.toString() : '')
          )
        } else {
          leadIntakeInput = _.set(leadIntakeInput, key, value)
        }
      })

    leadIntakeInput = _.set(
      leadIntakeInput,
      'properties',
      // @ts-ignore TODO handle multi properties and user fields
      [leadIntakeInput.property]
    )
    // @ts-ignore
    delete leadIntakeInput.property
    // @ts-ignore
    delete leadIntakeInput.__computed__

    return { ...leadIntakeInput }
  }

  const onSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    const leadIntakeInput = removeUnusedLeadAttributes()
    try {
      setIsLoading(true)
      let response: UserCreateResponse = {}
      if (!user?.logged_in) {
        response = (await UserService.create('/apps/api/signup', {
          accountCreationIntent: 'SELLING_NO_PASSWORD',
          firstName: leadIntakeInput.first_name,
          lastName: leadIntakeInput.last_name,
          email: leadIntakeInput.email,
          phone: leadIntakeInput.phone,
        })) as UserCreateResponse
      }
      if (!listingId) {
        createLead(
          e,
          leadIntakeInput,
          false,
          user?.user_id || response?.body?.user?.user_id
        )
      } else {
        onNextStep(e, listingId, false)
        setIsLoading(false)
      }
    } catch (error: any) {
      let errorMessage
      const { message = '' } = error as Error
      const errorList = error?.body?.errors
      const accountExistsErrorCode = errorList?.find((err: any) =>
        ['ACCOUNT_ALREADY_EXISTS', 'CONFIRMATION_PENDING'].includes(
          err.extensions?.specificErrorCode
        )
      )
      if (accountExistsErrorCode) {
        setLeadIntake({
          ...leadIntake,
          email: leadIntakeInput.email,
        })
        if (!listingId) {
          createLead(e, leadIntakeInput, true)
        } else {
          onNextStep(e, listingId, true)
          setIsLoading(false)
        }
      } else {
        if (!message) {
          const errorBody = (error as any).body?.errors
          if (errorBody?.length) {
            errorMessage = errorBody[0].message
          }
        } else {
          errorMessage = message
        }
        notifications.toast.open({
          role: NOTIFICATION_ROLES.ERROR,
          title: 'Something went wrong',
          message: errorMessage,
        })
        logger.error(JSON.stringify(error))
        setIsLoading(false)
      }
    }
  }

  if (isLastStep) {
    return null
  }

  const privacyStatementLink = `${configment.get(
    'AUCTIONCOM_HOST',
    ''
  )}${PRIVACY_STATEMENT_ROUTE}`

  const termsOfUseLink = `${configment.get(
    'AUCTIONCOM_HOST',
    ''
  )}${TERMS_OF_USE_ROUTE}`

  return (
    <section className={cx('buttons-section')}>
      {/* @ts-ignore */}
      {match({
        isSubmitStep,
        isFirstStep,
        hidePrevious: currentStep.hidePrevious,
        isConfirmAddress,
        isAuthentication,
      })
        .with({ isSubmitStep: true }, () => (
          <div>
            <div className={cx('spaced-out-buttons')}>
              <Button
                className={cx('button')}
                onClick={onPreviousStep}
                elmId='previous-button'
              >
                Previous
              </Button>
              <Button
                role='primary'
                className={cx('button')}
                onClick={onSubmit}
                disabled={stepProgression.isNextDisabled}
                elmId='submit-button'
              >
                Continue to Listing
              </Button>
            </div>
            <div className={cx('spaced-out-legend')}>
              <Text size='tiny' weight='heavy'>
                New here? No problem. We'll automatically create a free account
                for you. By clicking the ‘Continue’ button, you agree to
                Auction.com's{' '}
                <a href={termsOfUseLink} target='_blank' rel='noreferrer'>
                  Terms of Use
                </a>{' '}
                and{' '}
                <a href={privacyStatementLink} target='_blank' rel='noreferrer'>
                  Privacy Statement
                </a>
                .
              </Text>
            </div>
          </div>
        ))
        .with({ isFirstStep: true }, () => (
          <Button
            role='primary'
            className={cx('button')}
            onClick={onNextStep}
            disabled={stepProgression.isNextDisabled}
            loading={stepProgression.isCurrentlyValidating}
            elmId='start-button'
          >
            Start
          </Button>
        ))
        .with({ hidePrevious: true }, () => (
          <Button
            role='primary'
            className={cx('button')}
            onClick={onNextStep}
            disabled={stepProgression.isNextDisabled}
            elmId='next-button'
          >
            Next
          </Button>
        ))
        .with({ isConfirmAddress: true }, () => (
          <>
            <Button
              className={cx('button')}
              onClick={onPreviousStep}
              role='secondary'
              elmId='edit-address-button'
            >
              Edit Address
            </Button>
            <Button role='primary' onClick={onNextStep} elmId='next-button'>
              Next
            </Button>
          </>
        ))
        .with({ isAuthentication: true }, () => (
          <>
            <Button
              role='secondary'
              color={Button.ButtonColors.Gray}
              className={cx('button')}
              onClick={onPreviousStep}
              elmId='previous-button'
            >
              Previous
            </Button>
          </>
        ))
        .otherwise(() => (
          <>
            <Button
              className={cx('button')}
              onClick={onPreviousStep}
              elmId='previous-button'
            >
              Previous
            </Button>
            <Button
              role='primary'
              onClick={onNextStep}
              disabled={stepProgression.isNextDisabled}
              elmId='next-button'
            >
              Next
            </Button>
          </>
        ))}
    </section>
  )
}

export default ButtonSection
